import { useState, useEffect } from 'react';
import {
  Card,
  CardContent,
  CardDescription,
  CardHeader,
  CardTitle,
} from '@/components/ui/card';
import { Button } from '@/components/ui/button';
import { Input } from '@/components/ui/input';
import { useNotification } from '@/components/notifications';
import { Label } from '@/components/ui/label';
import { Play, X, Loader2 } from 'lucide-react';
import { Checkbox } from '@/components/ui/checkbox';
import { createTest } from '@/services/evaluation/create-test';
import { runTest } from '@/services/evaluation/run-test';
import { getEvaluations } from '@/services/evaluation/get-evaluations';
import { extractRunIdFromS3Key } from '@/services/evaluation/get-test-results';
import { Eval } from '@/types/evals-types';
import { generatePath } from 'react-router';
import { useOrgNavigate } from '@/hooks/use-org-navigate';
import { Textarea } from '@/components/ui/textarea';
import { v4 as uuidv4 } from 'uuid';
import { CreateTestRequest } from '@/types/evals-types';
import { useEvaluationStore } from '@/stores/evaluation-store';
import { ROUTES } from '@/constants/routes';
import { useOrgStore } from '@/stores/org-store';
import { useTestRunStatusPolling } from '@/hooks/use-test-status-polling';

const MAX_CONTENT_LIMIT = 20; // max number of urls or plain text content

export const TestsConfigForm = () => {
  const [testName, setTestName] = useState('');
  const [selectedEvals, setSelectedEvals] = useState<string[]>([]);
  const [evaluations, setEvaluations] = useState<Eval[]>([]);

  const [isLoading, setIsLoading] = useState(false);

  const [customTexts, setCustomTexts] = useState<string[]>(['']);
  const navigate = useOrgNavigate();
  const notification = useNotification();

  const { pollTestRunStatus } = useTestRunStatusPolling();

  const { currentOrg } = useOrgStore();
  const [previousOrgId, setPreviousOrgId] = useState<string | null>(null);
  const { upsertTest } = useEvaluationStore();

  // Reset test selection when organization changes
  useEffect(() => {
    if (currentOrg) {
      const orgHasChanged =
        previousOrgId !== null && previousOrgId !== currentOrg.orgid;

      if (orgHasChanged) {
        // Reset test selection and form state

        // Reset form
        setTestName('');
        setSelectedEvals([]);
        setCustomTexts(['']);
      }

      setPreviousOrgId(currentOrg.orgid);
    }
  }, [currentOrg, previousOrgId]);

  useEffect(() => {
    // Fetch evaluations from API
    const fetchEvaluations = async () => {
      try {
        const evals = await getEvaluations();
        setEvaluations(evals);
      } catch (error) {
        console.error('Error fetching evaluations', error);
        setEvaluations([]); // fallback to empty
      }
    };
    fetchEvaluations();
  }, []);

  const handleToggleEval = (evalId: string) => {
    setSelectedEvals((prev) => {
      if (!Array.isArray(prev)) {
        return [evalId];
      }
      return prev.includes(evalId)
        ? prev.filter((id) => id !== evalId)
        : [...prev, evalId];
    });
  };

  const handleAddCustomText = () => {
    setCustomTexts((prev) => {
      if (prev.length >= MAX_CONTENT_LIMIT) {
        return prev; // Don't add if already at max limit
      }
      return [...prev, ''];
    });
  };

  const handleCustomTextChange = (idx: number, value: string) => {
    setCustomTexts((prev) => prev.map((text, i) => (i === idx ? value : text)));
  };

  const handleRemoveCustomText = (idx: number) => {
    setCustomTexts((prev) => prev.filter((_, i) => i !== idx));
  };

  const handleRunTest = async () => {
    // Validation
    if (!testName) {
      alert('Please enter a test name');
      return;
    }

    if (customTexts.filter(Boolean).length === 0) {
      alert('Please add at least one URL or plain text');
      return;
    }

    if (selectedEvals.length === 0) {
      alert('Please select at least one evaluation');
      return;
    }

    setIsLoading(true);
    try {
      // 1. Create the test
      const testId = uuidv4();
      const testRequest: CreateTestRequest = {
        id: testId,
        name: testName,
        evaluations: selectedEvals,
        test_type: 'content_test',
        test_config: {
          contents: [...customTexts.filter(Boolean)],
        },
      };

      const createdTest = await createTest(testRequest);

      // 2. Start the test run and get S3 key
      const runResponse = await runTest(createdTest.id);
      const s3Key = runResponse.s3_keys[0];

      if (!s3Key) {
        throw new Error('Failed to start test run - no S3 key returned');
      }

      // 3. Extract runId from S3 key
      const runId = extractRunIdFromS3Key(s3Key);

      upsertTest(createdTest);

      // 4. Start polling for test completion
      pollTestRunStatus(createdTest.id, runId, {
        onSuccess: () => {
          // Success - test completed, navigate to results
          setIsLoading(false);
          navigate(
            generatePath(ROUTES.EVALUATION.TESTS_SETTINGS, {
              testId: createdTest.id,
            }),
          );
        },
        onError: (errorMessage) => {
          setIsLoading(false);
          notification.error(errorMessage);
          // Still navigate to results page since test was created
          navigate(
            generatePath(ROUTES.EVALUATION.TESTS_SETTINGS, {
              testId: createdTest.id,
            }),
          );
        },
      });
    } catch (error) {
      console.error('Error creating test', error);
      notification.error('Error creating test. Please try again.');
      setIsLoading(false);
    }
  };

  return (
    <div className='space-y-6 w-full'>
      <h2 className='text-3xl font-bold tracking-tight'>New Test</h2>
      <p className='text-muted-foreground'>
        Configure and run a new evaluation test.
      </p>

      <Card>
        <CardHeader>
          <CardTitle>Run Test</CardTitle>
          <CardDescription>
            Configure and run evaluations on your AI models.
          </CardDescription>
        </CardHeader>
        <CardContent>
          <div className='space-y-6'>
            {/* Test Name */}
            <div className='space-y-2'>
              <Label htmlFor='title' className='label-required'>
                Test Name
              </Label>
              <Input
                id='title'
                placeholder='i.e. Creativity Evaluation'
                value={testName}
                onChange={(e) => setTestName(e.target.value)}
                disabled={isLoading}
              />
            </div>

            {/* Custom Text Inputs */}
            <div className='space-y-2'>
              <Label className='label-required'>Text Content</Label>
              <div className='flex flex-col gap-2'>
                {customTexts.map((text, idx) => (
                  <div key={idx} className='flex gap-2 items-center'>
                    <Textarea
                      value={text}
                      onChange={(e) =>
                        handleCustomTextChange(idx, e.target.value)
                      }
                      placeholder={`Paste URL or plain text here...`}
                      className='min-h-16 flex-1'
                      disabled={isLoading}
                    />
                    <Button
                      type='button'
                      variant='ghost'
                      onClick={() => handleRemoveCustomText(idx)}
                      disabled={isLoading}
                    >
                      <X className='w-4 h-4' />
                    </Button>
                  </div>
                ))}
                <Button
                  type='button'
                  variant='outline'
                  onClick={handleAddCustomText}
                  disabled={
                    customTexts.length >= MAX_CONTENT_LIMIT || isLoading
                  }
                >
                  + Add URL/Plain Text
                </Button>
              </div>
              <span className='text-xs text-muted-foreground'>
                You can paste URL or plain text content.
                {customTexts.length >= MAX_CONTENT_LIMIT && (
                  <span className='text-orange-600 ml-1'>
                    (Maximum reached)
                  </span>
                )}
              </span>
            </div>

            {/* Evaluations Multi-select */}
            <div className='space-y-2'>
              <div className='flex justify-between'>
                <Label htmlFor='evaluations' className='label-required'>
                  Select Evaluations
                </Label>
                <span className='text-xs text-muted-foreground'>
                  {selectedEvals.length} selected
                </span>
              </div>
              <div className='border rounded-md p-4 max-h-60 overflow-y-auto space-y-4'>
                {evaluations.map((eval_: Eval) => (
                  <div key={eval_.id} className='space-y-2'>
                    <div className='flex items-center space-x-2 p-2 hover:bg-slate-50 dark:hover:bg-slate-800 rounded'>
                      <Checkbox
                        id={`eval-${eval_.id}`}
                        checked={selectedEvals.includes(eval_.id)}
                        onCheckedChange={() => handleToggleEval(eval_.id)}
                        disabled={isLoading}
                      />
                      <div className='flex-1'>
                        <label
                          htmlFor={`eval-${eval_.id}`}
                          className='text-sm font-medium leading-none cursor-pointer flex items-center justify-between'
                        >
                          <span>{eval_.name}</span>
                          <span className='text-xs text-gray-500 px-1.5 py-0.5 bg-gray-100 rounded-full'>
                            {eval_.type === 'pass_fail' ? 'Pass/Fail' : 'Score'}
                          </span>
                        </label>
                        <p className='text-xs text-muted-foreground mt-1'>
                          {eval_.description}
                        </p>
                      </div>
                    </div>
                  </div>
                ))}
                {evaluations.length === 0 && (
                  <p className='text-sm text-muted-foreground text-center py-4'>
                    No evaluations available
                  </p>
                )}
              </div>
            </div>

            {/* Selected Evaluations */}
            {selectedEvals.length > 0 && (
              <div>
                <Label className='text-sm text-muted-foreground'>
                  Selected Evaluations
                </Label>
                <div className='flex flex-wrap gap-2 mt-2'>
                  {selectedEvals.map((evalId) => {
                    const eval_ = evaluations.find((e) => e.id === evalId);
                    return eval_ ? (
                      <div
                        key={eval_.id}
                        className='flex items-center gap-1 bg-muted text-muted-foreground text-sm px-2 py-1 rounded'
                      >
                        <span>{eval_.name}</span>
                        <Button
                          variant='ghost'
                          size='icon'
                          className='h-4 w-4'
                          onClick={() => handleToggleEval(eval_.id)}
                          disabled={isLoading}
                        >
                          <X className='h-3 w-3' />
                        </Button>
                      </div>
                    ) : null;
                  })}
                </div>
              </div>
            )}

            {/* Run Button */}
            <Button
              className='w-full mt-4 gap-2'
              onClick={handleRunTest}
              disabled={
                !testName ||
                customTexts.filter(Boolean).length === 0 ||
                selectedEvals.length === 0 ||
                isLoading
              }
            >
              {isLoading ? (
                <>
                  <Loader2 className='h-4 w-4 animate-spin' />
                  Running...
                </>
              ) : (
                <>
                  <Play className='h-4 w-4' />
                  Run Test
                </>
              )}
            </Button>
          </div>
        </CardContent>
      </Card>
    </div>
  );
};
